import { Box, Button, Typography, useTheme } from "@mui/material";
import { ReactComponent as FayeIcon } from "../../icons/FayeBrand.svg";
import { Mail, MessageSquareText } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";

interface TwoFactorVerificationMethodProps {
  onSelectMethod: (method: "email" | "sms") => void;
  onCancel: () => void;
  error?: string | null;
}

export default ({
  onSelectMethod,
  onCancel,
  error,
}: TwoFactorVerificationMethodProps) => {
  const theme = useTheme();

  const verificationButtonSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
    height: "80px",
    borderRadius: "12px",
    border: `1px solid ${theme.palette.border}`,
    backgroundColor: "#FFF",

    color: theme.palette.text.primary,
    fontSize: "16px",
    fontWeight: 500,

    "&:hover": {
      backgroundColor: theme.palette.teal[1],
      border: `1px solid ${theme.palette.border}`,
    },
  };

  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isMobile ? "white" : theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          justifyContent: "center",
          marginTop: isMobile ? "-10vh" : 0,
          width: "100%",
          height: isMobile ? "100vh" : "auto",
          maxWidth: isMobile ? "100%" : "505px",
          padding: "32px 32px 20px",
          backgroundColor: isMobile ? undefined : "#FFF",
          borderRadius: isMobile ? 0 : "24px",
          border: isMobile ? "none" : `1px solid ${theme.palette.gray[3]}`,
          boxShadow: isMobile ? "none" : theme.elevation[1],
        }}
      >
        <FayeIcon
          style={{
            flexShrink: 0,
            marginBottom: "20px",
            width: "48px",
          }}
        />

        <Typography
          variant="h2Serif"
          sx={{
            color: theme.palette.text.primary,
            marginBottom: "8px",
          }}
        >
          Let's make sure it's you
        </Typography>

        <Typography
          variant="body"
          sx={{
            color: theme.palette.text.primary,
            marginBottom: "24px",
          }}
        >
          How would you like to receive your one-time confirmation code?
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: "16px",
            width: "100%",
            marginBottom: "12px",
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelectMethod("sms")}
            sx={verificationButtonSx}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <MessageSquareText size={24} color={theme.palette.primary.main} />
              Text
            </Box>
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelectMethod("email")}
            sx={verificationButtonSx}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Mail size={24} color={theme.palette.primary.main} />
              Email
            </Box>
          </Button>
        </Box>

        {error && (
          <Typography
            variant="body"
            sx={{ color: theme.palette.error.main, marginBottom: "12px" }}
          >
            {error}
          </Typography>
        )}

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <Button
            variant="text"
            onClick={onCancel}
            sx={{ color: theme.palette.primary.main }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
